import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import {
  getPlanStatus,
  PlanStatus,
  showExpiryAlert,
  shouldBlockAccess,
} from "../utils/alertUtils";
import { OperationType } from "../types/operationType";

const PLAN_STATUS_QUERY_KEY = ["planStatus"];

export const usePlanStatus = () => {
  const queryClient = useQueryClient();

  const {
    data: planStatus,
    isLoading: loading,
    error,
    refetch,
  } = useQuery<PlanStatus>({
    queryKey: PLAN_STATUS_QUERY_KEY,
    queryFn: getPlanStatus,
    staleTime: 1 * 60 * 1000, // Consider data stale after 1 minute (shorter for plan validation)
    gcTime: 60 * 60 * 1000, // Keep in cache for 1 hour
    refetchOnWindowFocus: "always", // Force refetch on window focus (overrides global config)
    refetchOnMount: true, // Refetch when component mounts (login)
    refetchInterval: 60 * 60 * 1000, // Refetch every hour
    refetchIntervalInBackground: false, // Don't refetch when tab is in background
    retry: 2, // Retry failed requests twice
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });

  // Provide default values if no data yet
  const defaultPlanStatus: PlanStatus = {
    daysUntilExpiry: 0,
    isExpired: false,
    isNotRegistered: false,
    isExpiringSoon: false,
    isNewUnpaidUser: false,
    isNewPaidUser: false,
    canAccess: true,
    planType: "free",
    companyId: null,
    company: null,
    operationType: OperationType.ALL,
    user: null,
  };

  const safePlanStatus: PlanStatus = planStatus || defaultPlanStatus;

  // Wrapper for fetchPlanStatus to maintain API compatibility
  const fetchPlanStatus = useCallback(async () => {
    try {
      await refetch();
    } catch (error) {
      console.error("Error fetching plan status:", error);
    }
  }, [refetch]);

  const showAlert = useCallback(() => {
    const alertMessage = showExpiryAlert(safePlanStatus);
    if (safePlanStatus.isNotRegistered) {
      window.alert("Complete your profile to start using the app.");
      return;
    }
    if (alertMessage) {
      window.alert(alertMessage);
    }
  }, [safePlanStatus]);

  const checkAccess = useCallback(
    (allowDashboard = false) => {
      return !shouldBlockAccess(safePlanStatus, allowDashboard);
    },
    [safePlanStatus]
  );

  const blockNavigation = useCallback(
    (targetPath: string) => {
      if (safePlanStatus.isNotRegistered && targetPath !== "/profile") {
        showAlert();
        return true;
      }
      if (safePlanStatus.isExpired && targetPath !== "/dashboard") {
        showAlert();
        return false;
      }
      if (safePlanStatus.isExpiringSoon && targetPath !== "/dashboard") {
        return true;
      }
      return true;
    },
    [safePlanStatus, showAlert]
  );

  // Utility function to manually invalidate and refetch plan status
  const invalidatePlanStatus = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: PLAN_STATUS_QUERY_KEY });
  }, [queryClient]);

  // Utility function to update plan status optimistically (useful for immediate UI updates)
  const updatePlanStatusOptimistic = useCallback(
    (newStatus: Partial<PlanStatus>) => {
      queryClient.setQueryData(
        PLAN_STATUS_QUERY_KEY,
        (oldData: PlanStatus | undefined) => ({
          ...(oldData || defaultPlanStatus),
          ...newStatus,
        })
      );
    },
    [queryClient]
  );

  return {
    planStatus: safePlanStatus,
    loading: loading || !planStatus, // Loading if query is loading or no data yet
    error,
    fetchPlanStatus,
    showAlert,
    checkAccess,
    blockNavigation,
    // Additional React Query utilities
    invalidatePlanStatus,
    updatePlanStatusOptimistic,
    user: safePlanStatus.user,
  };
};
